import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scroll the element matching the current `location.hash` into view, on cold
 * load (deep link like `/#pricing`) and whenever the hash changes (in-page nav
 * clicks). The match is restricted to an explicit allowlist of known section
 * ids — the hash is NEVER used to query an arbitrary, attacker-influenced
 * selector (Corridor: anchor targets must be controlled literals).
 *
 * Smooth where the user allows motion; a `scroll-mt-*` utility on the anchored
 * wrappers keeps the sticky header from clipping the target. A short rAF defers
 * the scroll until after the section has painted, which matters when the home
 * route's lazy chunks/sections mount on the same tick as the hash is read.
 *
 * @param allowedIds - The section ids this route knows how to scroll to.
 */
export function useHashScroll(allowedIds: readonly string[]): void {
  // `key` changes on every navigation — even a Link to the hash you're already
  // at — so depending on it (not just `hash`) makes a repeat click re-scroll.
  // Without it, clicking "Features" again after scrolling away would be a
  // dead no-op (react-router emits a navigation but `hash` is unchanged).
  const { hash, key } = useLocation();

  useEffect(() => {
    if (!hash) return;

    // Strip the leading "#". The hash is author-fixed in our nav, but decode +
    // allowlist anyway so a hand-typed/escaped hash can never reach an
    // arbitrary selector.
    const id = decodeURIComponent(hash.slice(1));
    if (!allowedIds.includes(id)) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // Defer to the next frame so the target is mounted + laid out before we
    // measure its position.
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
    });

    return () => {
      cancelAnimationFrame(raf);
    };
    // `key` is included so a repeat navigation to the same hash re-scrolls.
  }, [hash, key, allowedIds]);
}
